import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Geolocation } from '@capacitor/geolocation';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Flame, HeartPulse, Loader2, MapPin, ShieldAlert, UserPlus } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { Card } from '@shared/ui/containers/Card';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { platform } from '@shared/lib/platform';
import { toast } from '@shared/ui/feedback/Toast';
import {
  triggerPanic,
  cancelPanic,
  fetchEmergencyTypes,
  fetchMyPanicLogs,
  isActivePanicStatus,
  type PanicLog,
} from './api';
import { pageCopy } from '@shared/lib/pageCopy';
import { PanicContactsPanel } from './PanicContactsPanel';

const HOLD_MS = 1500;

type Tab = 'panic' | 'history' | 'contacts';
type Coords = { latitude: number; longitude: number; accuracy: number };

interface EmergencyTile {
  id: string;
  title: string;
  subtitle: string;
  icon: LucideIcon;
  /** Hex fill — comes from the emergency type's `bg_color`. */
  color: string;
}

const TONES = ['#b4544f', '#c98a4b', '#bd5b4a', '#7d8c5a', '#9c6b8a'];

const WHEN_FMT = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

function iconFor(name: string): LucideIcon {
  const n = name.toLowerCase();
  if (/medical|health|ambulance|clinic/.test(n)) return HeartPulse;
  if (/fire|smoke|evacuat/.test(n)) return Flame;
  if (/person|neighbour|guest|other/.test(n)) return UserPlus;
  return ShieldAlert;
}

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : WHEN_FMT.format(d);
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function logSortTime(log: Pick<PanicLog, 'created_at'>): number {
  const ts = Date.parse(log.created_at);
  return Number.isNaN(ts) ? 0 : ts;
}

/**
 * Panic / SOS — brand-tinted editorial header with in-header tabs. The Panic tab
 * prioritises hold-to-alert tiles (before the location card) so actions stay
 * above the fold; the page scrolls inside TabShell, which already pads for
 * the bottom nav (`pb-24`).
 */
export function PanicSos(): JSX.Element {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [holdingId, setHoldingId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [coords, setCoords] = useState<Coords | null>(null);
  const [locating, setLocating] = useState(true);
  const timerRef = useRef<number | null>(null);
  const holdPointerIdRef = useRef<number | null>(null);
  const holdCleanupRef = useRef<(() => void) | null>(null);
  const tabParam = searchParams.get('tab');
  const tab: Tab = tabParam === 'history' || tabParam === 'contacts' ? tabParam : 'panic';

  const typesQuery = useQuery({ queryKey: ['emergency-types'], queryFn: fetchEmergencyTypes });
  const logsQuery = useQuery({ queryKey: ['my-panic-logs'], queryFn: () => fetchMyPanicLogs() });
  const activeLogsQuery = useQuery({
    queryKey: ['my-panic-logs', 'active'],
    queryFn: () => fetchMyPanicLogs({ bucket: 'active' }),
  });

  const activeTypeIds = new Set((typesQuery.data ?? []).map((type) => type.emergency_type_id));
  const activeLogsSource = activeLogsQuery.data ?? logsQuery.data ?? [];
  const activeLogCandidates = [...activeLogsSource]
    .filter((log) => isActivePanicStatus(log.status) && !log.closed_at)
    .sort((a, b) => logSortTime(b) - logSortTime(a));
  const activeLog = typesQuery.isLoading
    ? null
    : activeLogCandidates.find((log) => !typesQuery.isSuccess || activeTypeIds.has(log.emergency_type_id)) ?? null;

  // Capture the resident's location once on mount — shown on the map below,
  // and dispatched (and stored) with any alert they raise.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 8000 });
        if (!cancelled) {
          setCoords({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          });
        }
      } catch {
        /* location optional */
      } finally {
        if (!cancelled) setLocating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const tiles: EmergencyTile[] = (typesQuery.data ?? []).map((t, i) => ({
    id: t.emergency_type_id,
    title: t.name,
    subtitle: 'Tap and hold to dispatch',
    icon: iconFor(t.name),
    color: t.bg_color || TONES[i % TONES.length]!,
  }));

  const clearHold = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    holdCleanupRef.current?.();
    holdCleanupRef.current = null;
    holdPointerIdRef.current = null;
    setHoldingId(null);
  };

  useEffect(() => () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
    }
    holdCleanupRef.current?.();
  }, []);

  const setTab = (nextTab: Tab) => {
    const nextParams = new URLSearchParams(searchParams);
    if (nextTab === 'panic') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', nextTab);
    }
    setSearchParams(nextParams, { replace: true });
  };

  const startHold = (event: ReactPointerEvent<HTMLButtonElement>, tile: EmergencyTile, key: string) => {
    if (!event.isPrimary || sending || timerRef.current) {
      return;
    }

    // NB: do NOT preventDefault or setPointerCapture here — both block the
    // scroll container, which is why dragging on a tile used to start the
    // countdown instead of scrolling. The tile's `touch-action: pan-y` lets
    // the browser own vertical scrolling; we only count a *stationary* press.
    clearHold();

    const pointerId = event.pointerId;
    const startX = event.clientX;
    const startY = event.clientY;
    setHoldingId(key);
    holdPointerIdRef.current = pointerId;

    if (platform.isNative()) {
      Haptics.impact({ style: ImpactStyle.Medium }).catch(() => undefined);
    }

    const cancelCurrentHold = () => {
      if (holdPointerIdRef.current !== pointerId) {
        return;
      }
      clearHold();
    };

    const handlePointerDone = (pointerEvent: PointerEvent) => {
      if (pointerEvent.pointerId !== pointerId) {
        return;
      }
      cancelCurrentHold();
    };

    // Movement beyond a small threshold means the user is scrolling, not
    // holding — abort the countdown and let the page scroll. (A scroll gesture
    // also emits pointercancel, but this catches the intent a touch earlier.)
    const handlePointerMove = (pointerEvent: PointerEvent) => {
      if (pointerEvent.pointerId !== pointerId) {
        return;
      }
      if (Math.hypot(pointerEvent.clientX - startX, pointerEvent.clientY - startY) > 12) {
        cancelCurrentHold();
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        cancelCurrentHold();
      }
    };

    document.addEventListener('pointerup', handlePointerDone);
    document.addEventListener('pointercancel', handlePointerDone);
    document.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('blur', cancelCurrentHold);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    holdCleanupRef.current = () => {
      document.removeEventListener('pointerup', handlePointerDone);
      document.removeEventListener('pointercancel', handlePointerDone);
      document.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('blur', cancelCurrentHold);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };

    timerRef.current = window.setTimeout(() => {
      clearHold();
      void fire(tile);
    }, HOLD_MS);
  };

  const fire = async (tile: EmergencyTile) => {
    setSending(true);

    let where = coords;
    if (!where) {
      try {
        const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 6000 });
        where = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        };
        setCoords(where);
      } catch {
        /* location optional */
      }
    }

    try {
      await triggerPanic({
        emergency_type_id: tile.id,
        latitude: where?.latitude,
        longitude: where?.longitude,
        metadata: {
          source: 'resident_app',
          triggered_at: new Date().toISOString(),
          accuracy_m: where?.accuracy,
        },
      });
      if (platform.isNative()) {
        Haptics.notification({ type: NotificationType.Success }).catch(() => undefined);
      }
      toast.success('Alert raised', 'Estate security has your location.');
      await queryClient.invalidateQueries({ queryKey: ['my-panic-logs'] });
    } catch {
      toast.danger('Could not raise alert', 'Call your estate security number directly.');
    } finally {
      setSending(false);
    }
  };

  const cancelMutation = useMutation({
    mutationFn: (logId: string) => cancelPanic(logId),
    onSuccess: () => {
      toast.success('Alert cancelled');
      void queryClient.invalidateQueries({ queryKey: ['my-panic-logs'] });
    },
    onError: (e) => toast.danger('Could not cancel', e instanceof Error ? e.message : undefined),
  });

  const sosCopy = pageCopy().panic.sos;

  return (
    <div className="min-h-full bg-paper">
      {/* Brand-tinted emergency header — editorial layout; tabs stay in the header band */}
      <HeroHeader
        collapsible
        eyebrow={sosCopy.eyebrow}
        title={sosCopy.title}
        subtitle={sosCopy.subtitle}
        tabs={[
          { value: 'panic', label: 'Panic' },
          { value: 'history', label: 'History' },
          { value: 'contacts', label: 'Contacts' },
        ]}
        activeTab={tab}
        onTabChange={setTab}
        tabsLabel="Panic sections"
      />

      {/* Panic tab — hold tiles first; location follows (scrolls above tab bar via TabShell) */}
      {tab === 'panic' ? (
        <div className="flex flex-col gap-4 px-5 pt-7">
          {activeLog ? (
            <ActiveAlertBanner
              log={activeLog}
              cancelling={cancelMutation.isPending}
              onCancel={() => cancelMutation.mutate(activeLog.panic_log_id)}
            />
          ) : null}

          {typesQuery.isLoading ? (
            // Loading placeholders that read as cards (sunken fill + tile shadow)
            // with a clear spinner — the bare skeletons were near-invisible on the
            // white background (sunken is ~4% off paper) so they looked broken.
            <div className="grid grid-cols-2 gap-3" aria-busy="true" aria-label="Loading emergency options">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="flex h-40 items-center justify-center rounded-2xl bg-sunken shadow-[0_4px_14px_rgba(0,0,0,0.10)]"
                >
                  <Loader2 className="h-7 w-7 animate-spin text-primary-600/70" strokeWidth={2} />
                </div>
              ))}
            </div>
          ) : typesQuery.isError ? (
            <ErrorState onRetry={() => typesQuery.refetch()} />
          ) : tiles.length === 0 ? (
            <EmptyState
              icon={<ShieldAlert className="h-7 w-7" strokeWidth={1.5} />}
              title="No emergency types configured"
              description="Your estate has not published any panic alert types yet. Contact your community office."
            />
          ) : (
            <div
              className={
                tiles.length === 1 ? 'grid grid-cols-1 gap-3' : 'grid grid-cols-2 gap-3'
              }
            >
              {tiles.map((tile) => {
                const key = tile.id!;
                const holding = holdingId === key;
                const Icon = tile.icon;
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={sending}
                    style={{ backgroundColor: tile.color, touchAction: 'pan-y' }}
                    onPointerDown={(event) => startHold(event, tile, key)}
                    onContextMenu={(event) => event.preventDefault()}
                    className={
                      'relative flex h-40 select-none flex-col overflow-hidden rounded-2xl p-4 text-left text-white transition-transform duration-fast ' +
                      (holding ? 'scale-[0.98]' : '')
                    }
                  >
                    <span
                      aria-hidden
                      className="absolute inset-x-0 bottom-0 origin-bottom bg-white/20"
                      style={{
                        height: '100%',
                        transform: holding ? 'scaleY(1)' : 'scaleY(0)',
                        transition: holding ? `transform ${HOLD_MS}ms linear` : 'transform 150ms ease',
                      }}
                    />
                    <span className="relative flex h-9 w-9 items-center justify-center rounded-pill bg-white/20">
                      <Icon className="h-4.5 w-4.5" strokeWidth={1.75} />
                    </span>
                    <span className="relative mt-auto">
                      <span className="block font-display text-headline leading-tight">{tile.title}</span>
                      <span className="mt-0.5 block text-caption text-white/75">{tile.subtitle}</span>
                      <span className="mt-2 block text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-white/90">
                        {holding ? 'Keep holding…' : 'Hold to alert'}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <LocationMap coords={coords} locating={locating} compact />
        </div>
      ) : null}

      {/* ── History tab ──────────────────────────────────────────── */}
      {tab === 'history' ? (
        <div className="flex flex-col gap-3 px-5 pt-7">
          {logsQuery.isLoading ? (
            <>
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
            </>
          ) : (logsQuery.data ?? []).length === 0 ? (
            <EmptyState
              icon={<ShieldAlert className="h-7 w-7" strokeWidth={1.5} />}
              title="No alerts yet."
              description="Any panic alerts you raise will be listed here."
            />
          ) : (
            logsQuery.data!.map((log) => <HistoryRow key={log.panic_log_id} log={log} />)
          )}
        </div>
      ) : null}

      {/* ── Contacts tab ─────────────────────────────────────────── */}
      {tab === 'contacts' ? (
        <div className="px-5 pt-7">
          <PanicContactsPanel returnTo="/app/panic?tab=contacts" />
        </div>
      ) : null}

      {sending ? (
        // Solid backdrop. NOTE: the `primary-*` palette is defined as plain
        // `var(--primary-900)` (no <alpha-value> channel), so the `/80` opacity
        // modifier silently produces an invalid colour → fully transparent mask.
        // Use an opaque branded colour instead, with an inline rgba fallback so
        // the backdrop is guaranteed solid even before branding vars resolve.
        <div
          className="fixed inset-0 z-[70] flex flex-col items-center justify-center gap-5 bg-primary-900 text-white"
          style={{ backgroundColor: 'var(--primary-900, #0f172a)' }}
          role="status"
          aria-live="assertive"
        >
          <span className="relative flex h-24 w-24 items-center justify-center">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-pill bg-white/30" />
            <span className="relative flex h-24 w-24 items-center justify-center rounded-pill bg-white/15">
              <ShieldAlert className="h-12 w-12 text-white" strokeWidth={2.2} />
            </span>
          </span>
          <div className="flex items-center gap-2.5">
            <Loader2 className="h-6 w-6 animate-spin text-white" />
            <span className="font-display text-display-lg">Sending…</span>
          </div>
          <span className="text-label text-white/80">Alerting your emergency contacts</span>
        </div>
      ) : null}
    </div>
  );
}

/* ── Active alert banner ────────────────────────────────────────── */
function ActiveAlertBanner({
  log,
  cancelling,
  onCancel,
}: {
  log: PanicLog;
  cancelling: boolean;
  onCancel: () => void;
}): JSX.Element {
  return (
    <div className="rounded-2xl bg-primary-900 p-4 text-white">
      <div className="flex items-center gap-2">
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-pill bg-brass opacity-75" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-pill bg-brass" />
        </span>
        <span className="text-[0.625rem] font-semibold uppercase tracking-[0.16em] text-brass">
          Alert active
        </span>
      </div>
      <p className="mt-2 font-display text-headline text-white">{log.emergency_type_name}</p>
      <p className="mt-0.5 text-caption text-white/65">
        Triggered {fmtWhen(log.created_at)} · {statusLabel(log.status)}
      </p>
      <p className="mt-2 text-caption text-white/65">
        Estate security has your live location. Only cancel once you're safe.
      </p>
      <button
        type="button"
        onClick={onCancel}
        disabled={cancelling}
        className="mt-3 w-full rounded-pill bg-white py-3 text-label font-semibold text-primary-900 disabled:opacity-60"
      >
        {cancelling ? 'Cancelling…' : "I'm safe — cancel alert"}
      </button>
    </div>
  );
}

/* ── Location map ───────────────────────────────────────────────── */
function LocationMap({
  coords,
  locating,
  compact = false,
}: {
  coords: Coords | null;
  locating: boolean;
  compact?: boolean;
}): JSX.Element {
  const statusLine = coords
    ? `Included with alerts · ±${Math.round(coords.accuracy)} m`
    : locating
      ? 'Locating you…'
      : 'Location optional — allow access so responders can find you';

  if (compact) {
    return (
      <Card noGrain className="overflow-hidden rounded-xl p-0">
        <div className="flex items-center gap-2 px-4 pt-3 pb-2">
          <MapPin className="h-4 w-4 shrink-0 text-primary-700" strokeWidth={1.75} />
          <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.13em] text-brass">
            Your location
          </span>
        </div>
        <div className="relative h-64 bg-sunken sm:h-72">
          {coords ? (
            <iframe
              title="Your current location"
              className="absolute inset-0 h-full w-full border-0 pointer-events-none"
              src={`https://www.google.com/maps?q=${coords.latitude},${coords.longitude}&z=16&output=embed`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : (
            <div className="flex h-full items-center justify-center px-3">
              <p className="text-caption text-ink-muted">{locating ? 'Locating…' : 'No GPS fix'}</p>
            </div>
          )}
        </div>
        <p className="px-4 py-2.5 text-caption text-ink-muted">{statusLine}</p>
      </Card>
    );
  }

  return (
    <Card noGrain className="overflow-hidden rounded-xl p-0">
      <div className="flex items-center gap-2 px-4 pt-3 pb-2">
        <MapPin className="h-4 w-4 text-primary-700" strokeWidth={1.75} />
        <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.13em] text-brass">
          Your location
        </span>
      </div>
      <div className="relative h-44 bg-sunken">
        {coords ? (
          <iframe
            title="Your current location"
            className="absolute inset-0 h-full w-full border-0"
            src={`https://www.google.com/maps?q=${coords.latitude},${coords.longitude}&z=16&output=embed`}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-1 px-6 text-center">
            <MapPin className="h-6 w-6 text-ink-muted" strokeWidth={1.5} />
            <p className="text-body-sm text-ink-secondary">
              {locating ? 'Locating you…' : 'Location unavailable'}
            </p>
            {!locating ? (
              <p className="text-caption text-ink-muted">
                Allow location access so responders can reach you.
              </p>
            ) : null}
          </div>
        )}
      </div>
      {coords ? (
        <p className="px-4 py-2.5 text-caption text-ink-muted">
          Sent to responders when you raise an alert · accuracy ±{Math.round(coords.accuracy)} m
        </p>
      ) : null}
    </Card>
  );
}

/* ── History row ────────────────────────────────────────────────── */
function HistoryRow({ log }: { log: PanicLog }): JSX.Element {
  const active = isActivePanicStatus(log.status);
  const tone = active
    ? 'bg-danger/15 text-danger'
    : log.status === 'resolved'
      ? 'bg-primary-50 text-primary-700'
      : 'bg-sunken text-ink-secondary';
  return (
    <Card noGrain className="flex items-center gap-3 rounded-xl p-3.5">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700">
        <ShieldAlert className="h-5 w-5" strokeWidth={1.5} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-display text-headline leading-tight text-ink truncate">
          {log.emergency_type_name}
        </p>
        <p className="mt-0.5 text-caption text-ink-muted">{fmtWhen(log.created_at)}</p>
      </div>
      <span
        className={
          'shrink-0 rounded-pill px-2.5 py-1 text-[0.625rem] font-semibold uppercase tracking-wide ' +
          tone
        }
      >
        {statusLabel(log.status)}
      </span>
    </Card>
  );
}
